<template>
  <div id="app" class="d-flex flex-column">
    <header>
      <a href="#main-content" class="visually-hidden skip-to-content">Skip to content</a>

      <nav class="navbar navbar-dark navbar-expand-md bg-primary text-light">
        <div class="container-lg">
          <div class="d-flex flex-nowrap justify-content-between flex-grow-1">
            <a class="navbar-brand text-wrap d-flex align-items-center gap-2 lh-1"
              :href="brandUrl"
            >
              <img src="@/assets/hhs-bird-logo.png"
                alt="HHS logo"
              />
              {{ title }}
            </a>

            <button type="button"
              class="navbar-toggler btn fs-6 border-0 text-nowrap"
              :aria-controls="uniqName('navbar-content')"
              :aria-expanded="!collapse"
              aria-label="Toggle navigation"
              @click="collapse = !collapse"
            >
              {{ collapse ? 'Explore' : 'Close' }}
              <span :class="`bi bi-${collapse ? 'plus' : 'x'}`"></span>
            </button>
          </div>

          <div :id="uniqName('navbar-content')"
            :class="[
              'navbar-collapse flex-grow-0',
              { collapse }
            ]"
          >
            <slot name="navbar"></slot>
          </div>
        </div>
      </nav>

      <AlertMessage type="warning"
        :show="showScheduledMaintenance"
        inner-class="container-lg"
        outer-class="shadow-sm small"
        slim
      >
        This site is scheduled to be offline for maintenance between
        {{ maintenanceSchedule.startDate | formatJsonDateTime }}
        and
        {{ maintenanceSchedule.endDate | formatJsonDateTime }}.
      </AlertMessage>
    </header>

    <main id="main-content" class="flex-grow-1">
      <div class="container-lg py-5">
        <div class="row g-5">
          <div class="col-md-12">
            <router-view></router-view>
          </div>
        </div>
      </div>
    </main>

    <footer>
      <div class="bg-primary text-light">
        <div class="container-lg py-4">
          <slot name="footer"></slot>
        </div>
      </div>
      <div>
        <div class="container-lg">
          <slot name="post-footer"></slot>
        </div>
      </div>
      <div class="text-center my-2 small">
        Powered by <a target="_blank" href="https://ssinc.com/">Sole Solutions, Inc.</a>
      </div>
    </footer>

  </div>
</template>

<script>
import { isAfter, parseJSON } from 'date-fns';
import AlertMessage from '@/components/AlertMessage';
import { getMaintenanceSchedule } from '@/api';
import { formatJsonDateTime } from '@/helpers/dateHelpers';

export default {
  inject: ['config'],

  filters: { formatJsonDateTime },

  props: {
    brandUrl: {
      type: String,
      default: () => '/'
    }
  },

  components: { AlertMessage },

  data: () => ({
    collapse: true,
    maintenanceSchedule: {
      startDate: null,
      endDate: null
    }
  }),

  computed: {
    showScheduledMaintenance: ({ maintenanceSchedule: sched }) =>
      !!sched.startDate && isAfter(parseJSON(sched.endDate), new Date())
  },

  methods: {
    async checkForMaintenance() {
      this.maintenanceSchedule = await getMaintenanceSchedule();
    }
  },

  created() {
    this.checkForMaintenance();

    this.title = this.config.siteTitle;
    window.document.title = this.title;
  }
};
</script>

<style lang="scss">
@import "@/styles/common";
@import "@/styles/scroll";
@import "@/styles/transitions";
@import "~bootstrap/scss/variables";
@import "~bootstrap/scss/mixins";

#app {
  height: 100vh;
}

.container-lg {
  max-width: 960px;
}

.navbar a { color: inherit; }

@include media-breakpoint-down(sm) {
  .navbar-brand {
    transform: scale(0.8) translateX(-1em);
  }
}

.skip-to-content:focus { position: static !important; }
</style>
